/**
 * AI Chat Assistant Page
 * Story: E007-S028
 * Madgicx-inspired conversational AI for campaign analytics
 */

import { useState, useEffect, useRef } from 'react';
import {
  createConversation,
  getConversations,
  getConversation,
  sendMessage,
  updateConversation,
  deleteConversation,
  getQuickQuestions,
} from '../lib/aiChatApi';
import type {
  Conversation,
  ChatMessage,
  QuickQuestion,
  ConversationContext,
} from '../types/ai-chat';
import { useAuth } from '../context/AuthContext';

// Icons
const ChatIcon = () => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);

const HistoryIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const StarIcon = ({ filled }: { filled: boolean }) => (
  <svg className={`w-5 h-5 ${filled ? 'text-yellow-400 fill-current' : ''}`} fill={filled ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
  </svg>
);

const SendIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
  </svg>
);

const PlusIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

export default function AIChatPage() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [quickQuestions, setQuickQuestions] = useState<QuickQuestion[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [historyTab, setHistoryTab] = useState<'recent' | 'favorites'>('recent');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContext, setSelectedContext] = useState<ConversationContext | null>(null);
  const [showAssetSelector, setShowAssetSelector] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load initial data
  useEffect(() => {
    loadConversations();
    loadQuickQuestions();
  }, []);

  const loadConversations = async () => {
    try {
      const response = await getConversations({ limit: 50 });
      setConversations(response.conversations || []);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    }
  };

  const loadQuickQuestions = async () => {
    try {
      const response = await getQuickQuestions();
      setQuickQuestions(response.questions || []);
    } catch (error) {
      console.error('Failed to load quick questions:', error);
      // Default questions
      setQuickQuestions([
        { id: '1', text: 'How did my ads perform this week compared to last week?', category: 'performance' },
        { id: '2', text: 'Which ad creatives are driving the highest conversions?', category: 'creatives' },
        { id: '3', text: 'Where is my Meta Ads budget being wasted?', category: 'budget' },
        { id: '4', text: 'Which campaigns and ads give the highest returns?', category: 'roi' },
        { id: '5', text: 'Are there any sudden performance drops I should worry about?', category: 'alerts' },
        { id: '6', text: 'Which ads are showing fatigue? Do I need to refresh any ads?', category: 'optimization' },
      ]);
    }
  };

  const handleNewChat = async () => {
    try {
      setIsLoading(true);
      const response = await createConversation(selectedContext || undefined);
      const newConversation: Conversation = {
        id: response.conversation_id,
        title: 'New Conversation',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        is_favorite: false,
        message_count: 0,
      };
      setCurrentConversation(newConversation);
      setMessages([]);
      setConversations(prev => [newConversation, ...prev]);
    } catch (error) {
      console.error('Failed to create conversation:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectConversation = async (conversation: Conversation) => {
    try {
      setIsLoading(true);
      const fullConversation = await getConversation(conversation.id);
      setCurrentConversation(fullConversation);
      setMessages(fullConversation.messages || []);
      setShowHistory(false);
    } catch (error) {
      console.error('Failed to load conversation:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (messageText?: string) => {
    const text = messageText || inputMessage;
    if (!text.trim() || isSending) return;

    // Create new conversation if needed
    let conversationId = currentConversation?.id;
    if (!conversationId) {
      try {
        const response = await createConversation(selectedContext || undefined);
        conversationId = response.conversation_id;
        const newConversation: Conversation = {
          id: conversationId,
          title: text.slice(0, 50) + (text.length > 50 ? '...' : ''),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          is_favorite: false,
          message_count: 0,
        };
        setCurrentConversation(newConversation);
        setConversations(prev => [newConversation, ...prev]);
      } catch (error) {
        console.error('Failed to create conversation:', error);
        return;
      }
    }

    // Add user message immediately
    const userMessage: ChatMessage = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: text,
      created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsSending(true);

    try {
      const response = await sendMessage(conversationId, text, selectedContext || undefined);
      
      // Update with actual AI response
      const assistantMessage: ChatMessage = {
        id: response.assistant_message_id,
        role: 'assistant',
        content: response.response,
        created_at: new Date().toISOString(),
        metadata: response.metadata,
      };
      setMessages(prev => [...prev, assistantMessage]);
      
      // Update conversation title if first message
      if (currentConversation && currentConversation.message_count === 0) {
        setCurrentConversation(prev => prev ? {
          ...prev,
          title: text.slice(0, 50) + (text.length > 50 ? '...' : ''),
          message_count: 2,
        } : null);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      // Add error message
      setMessages(prev => [...prev, {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: 'Sorry, I encountered an error processing your request. Please try again.',
        created_at: new Date().toISOString(),
      }]);
    } finally {
      setIsSending(false);
    }
  };

  const handleToggleFavorite = async (conversation: Conversation) => {
    try {
      await updateConversation(conversation.id, { is_favorite: !conversation.is_favorite });
      setConversations(prev => prev.map(c => 
        c.id === conversation.id ? { ...c, is_favorite: !c.is_favorite } : c
      ));
      if (currentConversation?.id === conversation.id) {
        setCurrentConversation(prev => prev ? { ...prev, is_favorite: !prev.is_favorite } : null);
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  };

  const handleDeleteConversation = async (conversation: Conversation) => {
    if (!confirm('Delete this conversation?')) return;
    try {
      await deleteConversation(conversation.id);
      setConversations(prev => prev.filter(c => c.id !== conversation.id));
      if (currentConversation?.id === conversation.id) {
        setCurrentConversation(null);
        setMessages([]);
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error);
    }
  };

  const filteredConversations = conversations.filter(c => {
    if (historyTab === 'favorites' && !c.is_favorite) return false;
    if (searchQuery && !c.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="flex flex-col h-[calc(100vh-100px)] bg-white dark:bg-gray-900 rounded-lg shadow-lg overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 dark:bg-indigo-900 rounded-lg">
            <ChatIcon />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              AI Chat
              <span className="text-xs px-2 py-0.5 bg-indigo-100 dark:bg-indigo-800 text-indigo-700 dark:text-indigo-300 rounded-full">Beta</span>
            </h1>
            {selectedContext && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Context: {selectedContext.selected_campaigns?.length || 0} campaigns
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleNewChat}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <PlusIcon />
            New Chat
          </button>
          <button
            onClick={() => setShowAssetSelector(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
          >
            Select Assets to Analyze
          </button>
          <button
            onClick={() => setShowHistory(!showHistory)}
            className={`p-2 rounded-lg border ${showHistory ? 'bg-indigo-100 dark:bg-indigo-900 border-indigo-300' : 'border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
          >
            <HistoryIcon />
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-6">
            {messages.length === 0 ? (
              /* Welcome Screen */
              <div className="flex flex-col items-center justify-center h-full text-center">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
                  Hi{user?.email ? ` ${user.email.split('@')[0]}` : ''}, What would you like to know?
                </h2>
                <p className="text-gray-500 dark:text-gray-400 mb-6">
                  Ask a question or pick a quick option below to get started.
                </p>
                <button className="mb-8 px-4 py-2 text-sm text-indigo-600 dark:text-indigo-400 border border-indigo-300 dark:border-indigo-700 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/30">
                  What can I do with this chat?
                </button>
                
                {/* Quick Questions Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl">
                  {quickQuestions.map((q) => (
                    <button
                      key={q.id}
                      onClick={() => handleSendMessage(q.text)}
                      className="p-4 text-left bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-indigo-300 dark:hover:border-indigo-600 hover:shadow-md transition-all"
                    >
                      <div className="w-8 h-8 mb-3 flex items-center justify-center bg-indigo-100 dark:bg-indigo-900 rounded-lg">
                        <ChatIcon />
                      </div>
                      <p className="text-sm text-gray-700 dark:text-gray-300">{q.text}</p>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              /* Chat Messages */
              <div className="space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[70%] p-4 rounded-lg ${
                        message.role === 'user'
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white'
                      }`}
                    >
                      <p className="whitespace-pre-wrap">{message.content}</p>
                      <p className="text-xs mt-2 opacity-70">
                        {new Date(message.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                ))}
                {isSending && (
                  <div className="flex justify-start">
                    <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4">
                      <div className="flex space-x-2">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Input Area */}
          <div className="border-t border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-3">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                placeholder="Ask anything..."
                className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                disabled={isSending}
              />
              <button
                onClick={() => handleSendMessage()}
                disabled={!inputMessage.trim() || isSending}
                className="p-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <SendIcon />
              </button>
            </div>
            <p className="text-xs text-center text-gray-400 mt-2">
              GP Ads AI may make mistakes. Please verify responses with actual campaign data.
            </p>
          </div>
        </div>

        {/* History Sidebar */}
        {showHistory && (
          <div className="w-80 border-l border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex flex-col">
            {/* Tabs */}
            <div className="flex border-b border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setHistoryTab('recent')}
                className={`flex-1 py-3 text-sm font-medium ${historyTab === 'recent' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}
              >
                Recent
              </button>
              <button
                onClick={() => setHistoryTab('favorites')}
                className={`flex-1 py-3 text-sm font-medium ${historyTab === 'favorites' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}
              >
                Favorites
              </button>
            </div>

            {/* Search */}
            <div className="p-3">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* Conversation List */}
            <div className="flex-1 overflow-y-auto">
              {filteredConversations.map((conversation) => (
                <div
                  key={conversation.id}
                  className={`p-3 border-b border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 ${currentConversation?.id === conversation.id ? 'bg-indigo-50 dark:bg-indigo-900/30' : ''}`}
                  onClick={() => handleSelectConversation(conversation)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {conversation.title || 'Untitled conversation'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {new Date(conversation.updated_at).toLocaleDateString()}
                      </p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleFavorite(conversation);
                      }}
                      className="ml-2"
                    >
                      <StarIcon filled={conversation.is_favorite} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Asset Selector Modal */}
      {showAssetSelector && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Select Assets to Analyze</h3>
              <button onClick={() => setShowAssetSelector(false)} className="text-gray-500 hover:text-gray-700">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Select campaigns, ad sets, or ads to provide context for your questions.
              </p>
              <p className="text-center text-gray-500 py-8">
                Asset selection will be available when connected to your ad accounts.
              </p>
            </div>
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <button
                onClick={() => setShowAssetSelector(false)}
                className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={() => setShowAssetSelector(false)}
                className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
              >
                Apply Selection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
